import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import MUIProvider from '~/contexts/MUIProvider';

import {
  Button,
  Container,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';
import DealPageForm from '~/components/Cruises/pages/DealsPage/components/DealPageForm';
import { CruiseDealPage, CruiseDealPageCreation } from '~/components/Cruises/pages/DealsPage/types';

import { useDealCrawlerByIds } from '~/hooks/apis/cruises/useDealCrawlerByIds';
import useAppNavigation from '~/hooks/useAppNavigation';

import { formatUTCDateDateTimeWidget } from '~/services/TimeService';
import { CruiseDealCrawler, CruiseDealSailingCrawler } from '~/services/cruises/DealCrawlerService';
import DealPageService from '~/services/cruises/DealPageService';
import InclusionsService from '~/services/cruises/InclusionsService';

import { paramToArray } from '~/utils/url';

import DealCrawlerInclusionList, { InclusionForm } from './DealCrawlerInclusionList';
import DealCrawlerPageCreationSelectionModal from './DealCrawlerPageCreationSelectionModal';
import {
  DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH,
  DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH,
  buildDealCrawlerPageCreationSearch,
  getMainCrawlerIdFromSearch,
} from './paths';

export default function DealCrawlerPageCreationConfirm() {
  const { push, replace } = useAppNavigation();
  const location = useLocation();
  const crawlerIds = useMemo(
    () => paramToArray(new URLSearchParams(location.search).get('crawlerIds') ?? undefined) ?? [],
    [location.search],
  );
  const mainCrawlerId = getMainCrawlerIdFromSearch(location.search, crawlerIds);

  const { dealCrawlerById, dealCrawlerSailingById } = useDealCrawlerByIds(crawlerIds);

  const [isSaving, setIsSaving] = useState(false);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);

  const handleSelectionModalConfirm = useCallback(
    (selected: Array<CruiseDealCrawler>, mainCrawlerIdFromModal: string) => {
      const ids = selected.map((c) => c.id);
      const search = buildDealCrawlerPageCreationSearch({ crawlerIds: ids, mainCrawlerId: mainCrawlerIdFromModal });
      push(`${DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH}?${search}`);
      setSelectionModalOpen(false);
    },
    [push],
  );

  const handleSetMainCrawler = useCallback(
    (crawlerId: string) => {
      const search = buildDealCrawlerPageCreationSearch({ crawlerIds, mainCrawlerId: crawlerId });
      replace(`${DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH}?${search}`);
    },
    [replace, crawlerIds],
  );

  const [offerInclusions, setOfferInclusions] = useState<Array<InclusionForm>>([]);
  const [dealPageInclusions, setDealPageInclusions] = useState<Array<InclusionForm>>([]);

  const { enqueueSnackbar } = useSnackbar();

  const dealCrawler = (mainCrawlerId && dealCrawlerById[mainCrawlerId]) ?? null;

  const displayedCrawlers = useMemo(
    () => crawlerIds.map((id) => dealCrawlerById[id]).filter(Boolean),
    [crawlerIds, dealCrawlerById],
  );

  const crawlerDepartures = useMemo(() => {
    const allSailings = crawlerIds.flatMap((id) => dealCrawlerSailingById[id] ?? []);

    const deduplicatedSailings = allSailings.reduce((acc, sailing) => {
      const departureId = sailing.departure?.id;
      if (departureId && !acc.has(departureId)) {
        acc.set(departureId, sailing);
      }
      return acc;
    }, new Map<string, CruiseDealSailingCrawler>());

    return Array.from(deduplicatedSailings.values());
  }, [crawlerIds, dealCrawlerSailingById]);

  const crawlerPromotions = useMemo(() => {
    const mainSailings = mainCrawlerId ? (dealCrawlerSailingById[mainCrawlerId] ?? []) : [];
    const raw = mainSailings.flatMap((s) => s.rates?.flatMap((r) => r.promotions) ?? []).filter(Boolean);
    const seen = new Set<string>();
    return raw.filter((p) => {
      const key = String(p).trim().toLowerCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [mainCrawlerId, dealCrawlerSailingById]);

  // We need to re-render the deal page form when the selected crawlers or departures change
  const dealPageFormKey = useMemo(() => {
    const departureIds = crawlerDepartures
      .map((d) => d.departure?.id)
      .filter(Boolean)
      .sort()
      .join(',');
    return `${mainCrawlerId}-${departureIds}`;
  }, [mainCrawlerId, crawlerDepartures]);

  const promotionsKey = useMemo(() => {
    const promotions = crawlerPromotions.slice().sort().join('');
    return `${mainCrawlerId}-${promotions}`;
  }, [mainCrawlerId, crawlerPromotions]);

  useEffect(() => {
    const inclusions = crawlerPromotions.map((promotion) => ({
      id: promotion,
      description: promotion,
      isActive: true,
    }));
    setDealPageInclusions(inclusions);
    setOfferInclusions(inclusions);
  }, [promotionsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveInclusions = useCallback(
    async (dealPage: CruiseDealPage) => {
      if (!dealPageInclusions.length) {
        return null;
      }

      let offerInclusionId: string | null = null;

      const activeDealInclusions = dealPageInclusions.filter((inclusion) => inclusion.isActive);
      if (activeDealInclusions.length) {
        try {
          await Promise.all(
            activeDealInclusions.map((inclusion) =>
              DealPageService.createDealInclusion(dealPage.id, {
                description: inclusion.description,
                icon: 'check',
                isHighlighted: false,
              }),
            ),
          );
        } catch (error) {
          console.error(error);
          enqueueSnackbar('Error saving deal page inclusions', { variant: 'error' });
        }
      }

      const activeOfferInclusions = offerInclusions.filter((inclusion) => inclusion.isActive);
      if (activeOfferInclusions.length && crawlerDepartures.length && dealCrawler) {
        try {
          const createdInclusion = await InclusionsService.create({
            name: dealCrawler.dealPageName,
            startDate: dealPage.startDate,
            endDate: dealPage.endDate,
            type: 'STANDARD',
            fkVendorId: dealCrawler.vendorId,
            filters: crawlerDepartures.map(({ departure }) => ({
              filterId: departure.id,
              filterType: 'DEPARTURE',
            })),
            items: activeOfferInclusions.map((inclusion) => ({
              type: 'FREE_TEXT',
              description: inclusion.description,
              isHighlighted: false,
            })),
          });

          offerInclusionId = createdInclusion.id;
        } catch (error) {
          enqueueSnackbar('Error saving offer inclusions', { variant: 'error' });
        }
      }

      return offerInclusionId;
    },
    [dealPageInclusions, enqueueSnackbar, dealCrawler, crawlerDepartures, offerInclusions],
  );

  const handleSubmit = useCallback(
    async (formValues: CruiseDealPageCreation) => {
      setIsSaving(true);

      try {
        const createdDealPage = await DealPageService.create({
          ...formValues,
          startDate: formatUTCDateDateTimeWidget(new Date(formValues.startDate)),
          endDate: formatUTCDateDateTimeWidget(new Date(formValues.endDate)),
        });

        const inclusionId = await saveInclusions(createdDealPage);

        enqueueSnackbar('Deal page created successfully', { variant: 'success' });
        push(`/cruises/deals/${createdDealPage.id}/edit${inclusionId ? `?newOfferInclusionId=${inclusionId}` : ''}`);
      } catch (error) {
        enqueueSnackbar(`Error saving deal page: ${error}`, { variant: 'error' });
      } finally {
        setIsSaving(false);
      }
    },
    [enqueueSnackbar, push, saveInclusions],
  );

  useEffect(() => {
    if (crawlerIds.length === 0) {
      replace(DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH);
    }
  }, [crawlerIds.length, replace]);

  if (crawlerIds.length === 0) {
    return null;
  }

  if (!dealCrawler) {
    return (
      <MUIProvider>
        <Helmet>
          <title>Create Deal Page From Crawler Data</title>
        </Helmet>
        <Container>
          <PageHeader title="Deal Page from Crawler" backButton />
          <Stack direction="row" justifyContent="center" alignItems="center" minHeight={200}>
            <Spinner />
          </Stack>
        </Container>
      </MUIProvider>
    );
  }

  return (
    <MUIProvider>
      <Helmet>
        <title>Create Deal Page From Crawler Data</title>
      </Helmet>
      <Container>
        <PageHeader title="Deal Page from Crawler" backButton />
        <Stack direction="column" spacing={4}>
          <Stack direction="column" spacing={1} sx={{ mt: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h5">Crawled pages</Typography>
              <Button variant="contained" color="primary" onClick={() => setSelectionModalOpen(true)}>
                Select other crawled pages
              </Button>
            </Stack>
            <TableContainer sx={{ maxHeight: 320, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Deal Page Name</TableCell>
                    <TableCell>Source URL</TableCell>
                    <TableCell align="right">Main</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedCrawlers.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.dealPageName}</TableCell>
                      <TableCell>
                        <a href={row.sourceUrl} target="_blank" rel="noopener noreferrer">
                          {row.sourceUrl}
                        </a>
                      </TableCell>
                      <TableCell align="right">
                        {row.id === mainCrawlerId ? (
                          <Typography variant="caption" color="primary" fontWeight="bold">
                            Main
                          </Typography>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => handleSetMainCrawler(row.id)}
                          >
                            Set as main
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>

          <Divider />

          <DealPageForm
            key={dealPageFormKey}
            dealCrawler={dealCrawler}
            crawlerDepartures={crawlerDepartures}
            onSubmit={handleSubmit}
            isFetching={isSaving}
          >
            {crawlerPromotions.length > 0 && (
              <Stack direction="column" spacing={2} marginTop={4}>
                <DealCrawlerInclusionList
                  title="Deal Page Inclusions"
                  subtitle="These inclusions will be displayed at the top of the deal page"
                  inclusionList={dealPageInclusions}
                  setInclusionList={setDealPageInclusions}
                  isEditEnabled
                />

                <DealCrawlerInclusionList
                  title="Offer Inclusions"
                  subtitle="These inclusions will be displayed in the offer tile"
                  inclusionList={offerInclusions}
                  setInclusionList={setOfferInclusions}
                  isEditEnabled
                />
              </Stack>
            )}
          </DealPageForm>
        </Stack>
      </Container>

      <DealCrawlerPageCreationSelectionModal
        open={selectionModalOpen}
        onClose={() => setSelectionModalOpen(false)}
        onConfirm={handleSelectionModalConfirm}
        defaultVendorId={dealCrawler?.vendorId}
        selectedCrawlerIds={crawlerIds}
        mainCrawlerId={mainCrawlerId}
      />
    </MUIProvider>
  );
}
